import { useReducer, useCallback } from 'react';
import { FormState, LogbookTemplateEditData } from '../types/loogBookTypes';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

type FormAction =
  | { type: 'UPDATE_FIELD'; payload: { field: keyof Omit<FormState, 'file' | 'previewUrl'>; value: string } }
  | { type: 'SET_FILE'; payload: { file: File } }
  | { type: 'CLEAR_FILE' }
  | { type: 'RESET'; payload?: LogbookTemplateEditData };

const emptyState: FormState = {
  name: '',
  description: '',
  type: '',
  category: '',
  file: null,
  previewUrl: null,
};

const initializer = (initialData?: LogbookTemplateEditData): FormState => {
  if (!initialData) {
    return emptyState;
  }
  return {
    ...emptyState,
    ...initialData,
    previewUrl: initialData.imageUrl || null,
  };
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.payload.field]: action.payload.value };
    case 'SET_FILE':
      if (state.previewUrl && state.file) {
        URL.revokeObjectURL(state.previewUrl);
      }
      return {
        ...state,
        file: action.payload.file,
        previewUrl: URL.createObjectURL(action.payload.file),
        attachmentId: undefined,
      };
    case 'CLEAR_FILE':
      if (state.previewUrl && state.file) {
        URL.revokeObjectURL(state.previewUrl);
      }
      return { ...state, file: null, previewUrl: null, attachmentId: undefined };
    case 'RESET':
      if (state.previewUrl && state.file) {
        URL.revokeObjectURL(state.previewUrl);
      }
      return initializer(action.payload);
    default:
      return state;
  }
};

export const useLogbookFormState = (initialData?: LogbookTemplateEditData) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(formReducer, initialData, initializer);
  const handleInputChange = useCallback((field: keyof Omit<FormState, 'file' | 'previewUrl'>, value: string) => {
    dispatch({ type: 'UPDATE_FIELD', payload: { field, value } });
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(selectedFile.type)) {
        alert(t('logbook.invalidFileType', 'Invalid file type. Only JPG, JPEG, and PNG are allowed.'));
        event.target.value = '';
        return;
      }
  
      dispatch({ type: 'SET_FILE', payload: { file: selectedFile } });
      event.target.value = '';
    }
  }, []);  

  const handleFileRemove = useCallback(() => {
    dispatch({ type: 'CLEAR_FILE' });
  }, []);

  const handleRotate = useCallback(async () => {
    if (!state.previewUrl) return;

    let imgSrc: string = state.previewUrl;
    let revokeImgSrcAfter: boolean = false;
    let type: string = state.file?.type || '';
    let filename: string = state.file?.name || state.imageFilename || 'rotated_image';

    if (!state.file) {
      const response = await fetch(state.previewUrl);
      if (!response.ok) {
        toast.error(t('logbook.fetchImageFailed', 'Failed to fetch image'));
        return;
      }
      const blob = await response.blob();
      type = blob.type;
      imgSrc = URL.createObjectURL(blob);
      revokeImgSrcAfter = true;
    }

    const img = new Image();
    img.src = imgSrc;

    try {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
    } catch (e) {
      toast.error(t('logbook.loadImageFailed', 'Failed to load image'));
      if (revokeImgSrcAfter) URL.revokeObjectURL(imgSrc);
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = img.height;
    canvas.height = img.width;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      if (revokeImgSrcAfter) URL.revokeObjectURL(imgSrc);
      return;
    }

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(90 * Math.PI / 180);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    const quality = type.startsWith('image/jpeg') ? 1 : undefined;
    const newBlob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, quality));

    if (!newBlob) {
      if (revokeImgSrcAfter) URL.revokeObjectURL(imgSrc);
      return;
    }

    const ext = type.split('/')[1] || 'png';
    filename = filename.replace(/\.[^/.]+$/, "") + '.' + ext;

    const newFile = new File([newBlob], filename, { type });

    dispatch({ type: 'SET_FILE', payload: { file: newFile } });

    if (revokeImgSrcAfter) URL.revokeObjectURL(imgSrc);
  }, [state]);

  return {
    formState: state,
    handleInputChange,
    handleFileSelect,
    handleFileRemove,
    handleRotate,
  };
};